import React from "react";
import { Box, ButtonBase, Tooltip } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { TabScrollButtonProps } from "@mui/material/TabScrollButton";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useTranslation } from "react-i18next";
import { MainSelector } from "@Store";
import { ParticipationIcon } from "@Components/Icons";
import {
    hasAiAssistantAccess,
    hasPremiumFeatures,
} from "@Utils/accountEntitlements";

export const POLLS_TAB = "POLLS_TAB";
export const PARTICIPATION_TAB = "PARTICIPATION_TAB";
export const REPORT_TAB = "REPORT_TAB";
export const ASSISTANT_TAB = "ASSISTANT_TAB";

export type ChannelTabValue =
    | typeof POLLS_TAB
    | typeof PARTICIPATION_TAB
    | typeof REPORT_TAB
    | typeof ASSISTANT_TAB;

const TAB_ACTIVE_COLOR = "#4F5BD5";
const TAB_ACTIVE_TINT = "#EEF0F5";

interface ChannelTabsMenuProps {
    value: ChannelTabValue;
    onChange: (newValue: ChannelTabValue) => void;
    hasEventReport?: boolean;
}

const ChannelTabScrollButton: React.FC<TabScrollButtonProps> = ({
    direction,
    className,
    disabled,
    onClick,
}) => {
    const { t } = useTranslation();

    return (
        <ButtonBase
            className={className}
            disabled={disabled}
            onClick={onClick}
            aria-label={t(
                direction === "left"
                    ? "home.tabs.scroll_left"
                    : "home.tabs.scroll_right"
            )}
            sx={{
                flexShrink: 0,
                opacity: 0.8,
                "&.Mui-disabled": { opacity: 0 },
                "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: -2,
                },
            }}
        >
            {direction === "left" ? (
                <KeyboardArrowLeftIcon fontSize="small" />
            ) : (
                <KeyboardArrowRightIcon fontSize="small" />
            )}
        </ButtonBase>
    );
};

const tabLabelSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    lineHeight: 1,
};

const lockedTabSx = {
    opacity: 0.4,
    cursor: "not-allowed",
};

const tabIconSx = {
    fontSize: 20,
    color: "inherit",
};

const renderTabLabel = (
    label: string,
    icon: React.ReactNode,
    tooltip?: string
) => {
    const content = (
        <Box component="span" sx={tabLabelSx}>
            {icon}
            <Box component="span">{label}</Box>
        </Box>
    );

    if (!tooltip) {
        return content;
    }

    return (
        <Tooltip title={tooltip} placement="top">
            <span>{content}</span>
        </Tooltip>
    );
};

export const ChannelTabsMenu: React.FC<ChannelTabsMenuProps> = ({
    value,
    onChange,
    hasEventReport = false,
}) => {
    const { t } = useTranslation();
    const accountType = MainSelector((state) => state.auth.user.account?.type);
    const canUsePremiumFeatures = hasPremiumFeatures(accountType);
    const canUseAiAssistant = hasAiAssistantAccess(accountType);
    const reportsPremiumTooltip = t("home.tabs.reports_premium_tooltip");
    const aiAssistantPremiumTooltip = t(
        "home.tabs.ai_assistant_premium_tooltip"
    );
    const emptyReportTooltip = t("pages.eventInsights.report.emptyHeading");

    const handleChange = (
        _event: React.ChangeEvent<{}>,
        newValue: ChannelTabValue
    ) => {
        if (newValue === REPORT_TAB && !canUsePremiumFeatures) return;
        if (
            newValue === ASSISTANT_TAB &&
            (!canUseAiAssistant || !hasEventReport)
        )
            return;
        onChange(newValue);
    };

    return (
        <Box
            sx={{
                backgroundColor: "background.paper",
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                mt: 3,
            }}
        >
            <Tabs
                value={value}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                ScrollButtonComponent={ChannelTabScrollButton}
                aria-label={t("home.tabs.aria_label")}
                sx={{
                    minHeight: 48,
                    "& .MuiTabs-flexContainer": {
                        minWidth: "max-content",
                    },
                    "& .MuiTab-root": {
                        minHeight: 48,
                        minWidth: { xs: "auto", sm: 90 },
                        textTransform: "none",
                        fontWeight: "medium",
                        fontSize: "0.875rem",
                        borderTopLeftRadius: 10,
                        borderTopRightRadius: 10,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        px: { xs: 1.25, sm: 2 },
                        color: "text.secondary",
                        transition: (theme) =>
                            theme.transitions.create(
                                ["background-color", "color"],
                                {
                                    duration:
                                        theme.transitions.duration.shorter,
                                }
                            ),
                    },
                    "& .MuiTab-root:not(:last-child)": {
                        mr: 1,
                    },
                    "& .MuiTab-root.Mui-selected": {
                        color: TAB_ACTIVE_COLOR,
                        backgroundColor: TAB_ACTIVE_TINT,
                    },
                    "& .MuiTabs-scrollButtons": {
                        width: { xs: 32, sm: 40 },
                    },
                    "& .MuiTabs-indicator": {
                        height: 3,
                        backgroundColor: TAB_ACTIVE_COLOR,
                        borderTopLeftRadius: 3,
                        borderTopRightRadius: 3,
                    },
                }}
            >
                <Tab
                    value={POLLS_TAB}
                    label={renderTabLabel(
                        t("home.tabs.polls"),
                        <PollOutlinedIcon sx={tabIconSx} />
                    )}
                    disableRipple
                />
                <Tab
                    value={PARTICIPATION_TAB}
                    label={renderTabLabel(
                        t("home.tabs.participation"),
                        <ParticipationIcon sx={tabIconSx} />
                    )}
                    disableRipple
                />
                <Tab
                    value={REPORT_TAB}
                    label={renderTabLabel(
                        t("home.tabs.reports"),
                        <AutoGraphOutlinedIcon sx={tabIconSx} />,
                        canUsePremiumFeatures
                            ? undefined
                            : reportsPremiumTooltip
                    )}
                    disableRipple={!canUsePremiumFeatures}
                    sx={canUsePremiumFeatures ? undefined : lockedTabSx}
                />
                <Tab
                    value={ASSISTANT_TAB}
                    label={renderTabLabel(
                        t("home.tabs.ai_assistant"),
                        <AutoAwesomeOutlinedIcon sx={tabIconSx} />,
                        canUseAiAssistant
                            ? hasEventReport
                                ? undefined
                                : emptyReportTooltip
                            : aiAssistantPremiumTooltip
                    )}
                    disableRipple={!canUseAiAssistant || !hasEventReport}
                    sx={
                        canUseAiAssistant && hasEventReport
                            ? undefined
                            : lockedTabSx
                    }
                />
            </Tabs>
        </Box>
    );
};
